import React, { useState, useEffect } from 'react';
import { Container, Button, Spinner } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { FaSearch, FaEraser, FaFileExcel } from 'react-icons/fa';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import {
  consultarDemonstrativoFinanceiro,
  listarGruposEmpresaDemonstrativoFinanceiro,
  listarFamiliasDemonstrativoFinanceiro,
} from '~/Services/Relatorios/DemonstrativoFinanceiroService';
import { filtrosSchema } from './validationSchema';
import PainelFiltros from './components/PainelFiltros';
import TabelaResultado from './components/TabelaResultado';
import { exportarDemonstrativoCsv } from './exportar';

const construirValoresIniciais = () => ({
  codigoGrupoEmpresa: null,
  codigoFamilia: null,
});

const DemonstrativoFinanceiroPage = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(true);

  const [familias, setFamilias] = useState([]);
  const [carregandoFamilias, setCarregandoFamilias] = useState(false);

  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);

  // Carrega o combo de GE. Auto-seleciona quando a lista tem 1 item (paridade SetaGE).
  const [geAutoSelecionado, setGeAutoSelecionado] = useState(null);

  useEffect(() => {
    const carregarOpcoes = async () => {
      try {
        const resGE = await listarGruposEmpresaDemonstrativoFinanceiro();
        const lista = Array.isArray(resGE) ? resGE : [];
        setGruposEmpresa(lista);
        if (lista.length === 1) {
          setGeAutoSelecionado(lista[0].codigo);
        }
      } catch (err) {
        toast.error('Erro ao carregar opções de filtro.');
      } finally {
        setLoadingOpcoes(false);
      }
    };
    carregarOpcoes();
  }, []);

  // Carrega as famílias do GE selecionado (limpa quando o GE é desmarcado).
  const carregarFamilias = async (codigoGrupoEmpresa) => {
    if (!codigoGrupoEmpresa) {
      setFamilias([]);
      return;
    }
    setCarregandoFamilias(true);
    try {
      const res = await listarFamiliasDemonstrativoFinanceiro(codigoGrupoEmpresa);
      setFamilias(Array.isArray(res) ? res : []);
    } catch (err) {
      setFamilias([]);
      toast.error('Erro ao carregar famílias do GE.');
    } finally {
      setCarregandoFamilias(false);
    }
  };

  // Quando o GE é auto-selecionado (lista com 1 item), carrega as famílias dele.
  useEffect(() => {
    if (geAutoSelecionado) {
      carregarFamilias(geAutoSelecionado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geAutoSelecionado]);

  const handleGerar = async (values, { setSubmitting }) => {
    setLoading(true);
    setResultado(null);

    const filtros = {
      codigoGrupoEmpresa: values.codigoGrupoEmpresa,
      codigoFamilia: values.codigoFamilia,
    };

    try {
      const data = await consultarDemonstrativoFinanceiro(filtros);
      const normalizado = data || { familias: [], total_geral: {} };
      setResultado(normalizado);

      const temItens =
        normalizado.familias &&
        normalizado.familias.some((f) => f.itens && f.itens.length > 0);
      if (!temItens) {
        toast.info('Nenhuma informação encontrada.');
      }
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao gerar relatório.';
      toast.error(mensagem);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleLimpar = (resetForm) => {
    setResultado(null);
    setFamilias([]);
    resetForm();
  };

  const handleExportar = () => {
    try {
      exportarDemonstrativoCsv(resultado);
    } catch (err) {
      toast.error('Erro ao exportar.');
    }
  };

  if (loadingOpcoes) {
    return (
      <Container fluid className="px-3 py-3">
        <HeaderAzul title="Demonstrativo Financeiro" />
        <Loading label="Carregando opções..." />
      </Container>
    );
  }

  const resultadoTemItens =
    resultado &&
    resultado.familias &&
    resultado.familias.some((f) => f.itens && f.itens.length > 0);

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Demonstrativo Financeiro" />

      <Formik
        initialValues={{
          ...construirValoresIniciais(),
          codigoGrupoEmpresa: geAutoSelecionado,
        }}
        enableReinitialize
        validationSchema={filtrosSchema}
        onSubmit={handleGerar}
      >
        {({ values, errors, touched, setFieldValue, submitForm, resetForm }) => {
          const podeGerar = !!values.codigoGrupoEmpresa && !loading;

          const onChangeGrupoEmpresa = (codigo) => {
            setFieldValue('codigoGrupoEmpresa', codigo);
            setFieldValue('codigoFamilia', null);
            setResultado(null);
            carregarFamilias(codigo);
          };

          return (
            <Form>
              <PainelFiltros
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                gruposEmpresa={gruposEmpresa}
                familias={familias}
                carregandoFamilias={carregandoFamilias}
                onChangeGrupoEmpresa={onChangeGrupoEmpresa}
              />

              <div className="d-flex flex-wrap mb-3">
                <Button
                  variant="primary"
                  className="mr-2"
                  onClick={submitForm}
                  disabled={!podeGerar}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <FaSearch className="mr-1" /> Gerar
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="mr-2"
                  onClick={() => handleLimpar(resetForm)}
                  disabled={loading}
                >
                  <FaEraser className="mr-1" /> Limpar
                </Button>
                <Button
                  variant="success"
                  onClick={handleExportar}
                  disabled={loading || !resultadoTemItens}
                >
                  <FaFileExcel className="mr-1" /> Exportar
                </Button>
              </div>

              {loading && <Loading label="Gerando relatório (pode demorar)..." />}

              {!loading && resultado && (
                <TabelaResultado resultado={resultado} />
              )}
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default DemonstrativoFinanceiroPage;
